import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type { SidebarRole } from "./Sidebar";
import ProfilePanel from "./ProfilePanel";
import API from "../services/API";
import { getCurrentUser } from "../services/auth";
import { openChatbot } from "../services/chatbot";
import { notifySuccess } from "../services/notification.service";

interface NavbarProps {
 role?: SidebarRole;
 onToggleSidebar?: () => void;
 sidebarCollapsed?: boolean;
}

export default function Navbar({
 role: _roleProp,
 onToggleSidebar,
 sidebarCollapsed,
}: NavbarProps) {
 const [profileOpen, setProfileOpen] = useState(false);
 const navigate = useNavigate();
 const location = useLocation();
 const isOnSettings = location.pathname ==="/settings";
 const user = getCurrentUser();

 return (
 <>
 <header className="app-header">
 {/* Left: Hamburger toggle - single unified button */}
 <div className="flex items-center gap-3">
 {onToggleSidebar && (
 <button
 type="button"aria-label={
 sidebarCollapsed ?"Expand sidebar":"Collapse sidebar"}
 aria-expanded={!sidebarCollapsed}
 onClick={onToggleSidebar}
 className="group inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-all duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-role-primary focus:ring-offset-1">
 <svg
 className="h-5 w-5 transition-transform duration-200 group-hover:scale-110"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <line x1="3"y1="6"x2="21"y2="6"/>
 <line x1="3"y1="12"x2="21"y2="12"/>
 <line x1="3"y1="18"x2="21"y2="18"/>
 </svg>
 </button>
 )}
 </div>

 {/* Middle: Spacer */}
 <div className="flex-1"/>

 {/* Right: Notification → Settings → Help → Profile + Name + Role */}
 <div className="flex items-center gap-1 sm:gap-2">
 {/* Notification bell */}
 <NotificationBell />

 {/* Settings icon */}
 <button
 type="button"aria-label={isOnSettings ?"Close Settings":"Settings"}
 onClick={() =>
 isOnSettings ? navigate(-1) : navigate("/settings")
 }
 className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-role-primary focus:ring-offset-1">
 <svg
 className={`h-5 w-5 transition-transform duration-500 ${isOnSettings ?"rotate-180":"hover:rotate-90"}`}
 viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <circle cx="12"cy="12"r="3"/>
 <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
 </svg>
 </button>

 {/* Help icon — opens chatbot */}
 <button
 type="button"aria-label="Help"onClick={() => openChatbot()}
 className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-role-primary focus:ring-offset-1">
 <svg
 className="h-5 w-5"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <circle cx="12"cy="12"r="10"/>
 <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
 <line x1="12"y1="17"x2="12.01"y2="17"/>
 </svg>
 </button>

 {/* Divider */}
 <div className="mx-1 h-6 w-px bg-slate-200 hidden sm:block"/>

 {/* Profile avatar + Name + Role */}
 <button
 type="button"aria-label="Open profile menu"onClick={() => setProfileOpen(true)}
 className="flex items-center gap-2 sm:gap-3 rounded-lg px-1.5 sm:px-2 py-1.5 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-role-primary focus:ring-offset-1">
 <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500">
 <svg
 className="h-4 w-4 sm:h-5 sm:w-5"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="1.5"strokeLinecap="round"strokeLinejoin="round">
 <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
 <circle cx="12"cy="7"r="4"/>
 </svg>
 </div>
 {user && (
 <div className="hidden md:block text-right">
 <p className="text-sm font-semibold text-slate-900 leading-tight truncate max-w-[120px]">
 {user.name}
 </p>
 <p className="text-[11px] text-slate-500 leading-tight">
 {user.role ==="ADMIN"?"Admin": user.role ==="PROJECT_MANAGER"||
 (user.role as string) ==="DELIVERY_MANAGER"?"Manager": (user.role as string) ==="EMPLOYEE"?"Employee": user.role}
 </p>
 </div>
 )}
 </button>
 </div>
 </header>

 <ProfilePanel open={profileOpen} onClose={() => setProfileOpen(false)} />
 </>
 );
}

/* ── Notification Bell ── */
function NotificationBell() {
 const [open, setOpen] = useState(false);
 const [unread, setUnread] = useState(0);
 const [notifications, setNotifications] = useState<any[]>([]);
 const [loading, setLoading] = useState(false);
 const [broadcastText, setBroadcastText] = useState("");
 const [sending, setSending] = useState(false);

 const user = getCurrentUser();
 // Admin broadcast uses portal-level permission - ADMIN role can send announcements
 const canBroadcast = user?.role ==="ADMIN";

 // Poll unread count every 30s — only if authenticated
 useEffect(() => {
 if (!user) return;

 const fetchCount = () => {
 API.get("/api/notifications/unread-count")
 .then((res) => {
 if (res.data?.success) setUnread(res.data.data.count);
 })
 .catch(() => {});
 };
 fetchCount();
 const interval = setInterval(fetchCount, 30000);
 return () => clearInterval(interval);
 }, [user?.id]);

 const handleOpen = () => {
 setOpen(!open);
 if (!open && user) {
 setLoading(true);
 API.get("/api/notifications")
 .then((res) => {
 if (res.data?.success) {
 setNotifications(res.data.data.notifications || []);
 setUnread(res.data.data.unread_count || 0);
 }
 })
 .catch(() => {})
 .finally(() => setLoading(false));
 }
 };

 const handleMarkAllRead = () => {
 API.patch("/api/notifications/mark-read", {})
 .then(() => {
 setUnread(0);
 setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
 })
 .catch(() => {});
 };

 const handleBroadcast = async () => {
 if (!broadcastText.trim()) return;
 setSending(true);
 try {
 await API.post("/api/notifications/broadcast", {
 title: broadcastText.trim(),
 message: broadcastText.trim(),
 });
 notifySuccess("Broadcast Sent","Your announcement has been sent to all users.",
 );
 setBroadcastText("");
 } catch {
 } finally {
 setSending(false);
 }
 };

 const formatTime = (dateStr: string) => {
 const d = new Date(dateStr);
 const diff = Date.now() - d.getTime();
 const mins = Math.round(diff / 60000);
 if (mins < 1) return"now";
 if (mins < 60) return`${mins}m`;
 const hrs = Math.round(mins / 60);
 if (hrs < 24) return`${hrs}h`;
 const days = Math.round(hrs / 24);
 if (days < 7) return`${days}d`;
 return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short"});
 };

 const getTypeIcon = (type: string) => {
 if (type.includes("TASK")) return"🔲";
 if (type.includes("LEAVE")) return"📅";
 if (type.includes("WORKLOG") || type.includes("TIMESHEET")) return"⏱️";
 if (type.includes("PROJECT")) return"📁";
 if (type.includes("ISSUE")) return"⚠️";
 if (type.includes("MILESTONE")) return"🎯";
 if (type.includes("BROADCAST") || type.includes("SYSTEM")) return"📢";
 return"🔔";
 };

 return (
 <div className="relative">
 <button
 type="button"aria-label="Notifications"onClick={handleOpen}
 className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-700">
 <svg
 className="h-5 w-5"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
 <path d="M13.73 21a2 2 0 01-3.46 0"/>
 </svg>
 {unread > 0 && (
 <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white shadow-sm animate-pulse">
 {unread > 99 ?"99+": unread}
 </span>
 )}
 </button>

 {/* Panel */}
 {open && (
 <>
 <div className="fixed inset-0 z-40"onClick={() => setOpen(false)} />
 <div className="absolute right-0 top-full z-50 mt-2 w-[calc(100vw-2rem)] max-w-96 rounded-xl border border-slate-200 bg-white shadow-2xl animate-slide-down sm:w-96">
 {/* Header */}
 <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
 <div className="flex items-center gap-2">
 <h3 className="text-sm font-bold text-slate-900">
 All Notifications
 </h3>
 {unread > 0 && (
 <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-bold text-rose-600">
 {unread}
 </span>
 )}
 </div>
 <div className="flex items-center gap-2">
 {unread > 0 && (
 <button
 type="button"onClick={handleMarkAllRead}
 className="text-[10px] font-semibold text-rose-500 hover:text-rose-600">
 Mark all as read
 </button>
 )}
 <button
 type="button"onClick={() => setOpen(false)}
 className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
 <svg
 className="h-4 w-4"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2">
 <line x1="18"y1="6"x2="6"y2="18"/>
 <line x1="6"y1="6"x2="18"y2="18"/>
 </svg>
 </button>
 </div>
 </div>

 {/* Admin broadcast input */}
 {canBroadcast && (
 <div className="border-b border-slate-100 px-4 py-2.5">
 <div className="flex items-center gap-2">
 <input
 type="text"value={broadcastText}
 onChange={(e) => setBroadcastText(e.target.value)}
 onKeyDown={(e) => {
 if (e.key ==="Enter") handleBroadcast();
 }}
 placeholder="Send announcement to all users..."className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs placeholder:text-slate-400 focus:border-role-primary focus:outline-none"/>
 <button
 type="button"onClick={handleBroadcast}
 disabled={sending || !broadcastText.trim()}
 className="rounded-lg bg-role-primary px-3 py-1.5 text-[10px] font-semibold text-white hover:opacity-90 disabled:opacity-50">
 {sending ?"...":"Send"}
 </button>
 </div>
 </div>
 )}

 {/* Notification list */}
 <div className="max-h-[400px] overflow-y-auto">
 {loading ? (
 <p className="px-4 py-8 text-center text-xs text-slate-400">
 Loading notifications...
 </p>
 ) : notifications.length === 0 ? (
 <div className="px-4 py-8 text-center">
 <span className="text-2xl">🔔</span>
 <p className="mt-2 text-xs text-slate-500">
 No notifications yet.
 </p>
 </div>
 ) : (
 notifications.map((n, idx) => (
 <div
 key={n.notification_id}
 className={`flex gap-3 border-b border-slate-50 px-4 py-3 transition-colors duration-150 hover:bg-slate-50 animate-fade-in ${!n.is_read ?"bg-blue-50/30":""}`}
 style={{ animationDelay:`${Math.min(idx * 30, 180)}ms`}}
 >
 {/* Icon */}
 <div className="mt-0.5 shrink-0 text-base">
 {getTypeIcon(n.type ||"")}
 </div>
 {/* Content */}
 <div className="flex-1 min-w-0">
 <p
 className={`text-xs leading-snug ${!n.is_read ?"font-semibold text-slate-900":"text-slate-700"}`}
 >
 {n.title}
 </p>
 {n.message && n.message !== n.title && (
 <p className="mt-0.5 text-[10px] text-slate-500 line-clamp-2">
 {n.message}
 </p>
 )}
 <div className="mt-1 flex items-center gap-2 text-[9px] text-slate-400">
 <span>{formatTime(n.created_at)}</span>
 {n.actor_name && <span>· {n.actor_name}</span>}
 </div>
 </div>
 {/* Unread dot */}
 {!n.is_read && (
 <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-role-primary"/>
 )}
 </div>
 ))
 )}
 </div>
 </div>
 </>
 )}
 </div>
 );
}
